function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }
function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import mergeOperations from '../../util/shallowMerge';
import DEFAULT_STORE_OPERATIONS from './storeSwitcher.gql';
import { useAwaitQuery } from "../../hooks/useAwaitQuery";
import { react_paths } from './reactRouterPaths';
import { getStoreCode } from "@magento/venia-ui/lib/components/StoreCodeRoute/storeCodeRoute";
var removeLastSlash = function () {
  var link = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  if (link.charAt(link.length - 1) === "/") {
    return link.substring(0, link.length - 1);
  }
  return link;
};
var mapAvailableOptions = (config, stores) => {
  var {
    code: configCode
  } = config;
  return stores.reduce((map, store) => {
    var {
      category_url_suffix,
      code,
      default_display_currency_code: currency,
      locale,
      product_url_suffix,
      secure_base_media_url,
      store_group_code: storeGroupCode,
      store_group_name: storeGroupName,
      store_name: storeName,
      store_sort_order: sortOrder
    } = store;
    var isCurrent = code === configCode;
    var option = {
      category_url_suffix,
      code,
      currency,
      isCurrent,
      locale,
      product_url_suffix,
      secure_base_media_url,
      sortOrder,
      storeGroupCode,
      storeGroupName,
      storeName
    };
    return map.set(code, option);
  }, new Map());
};

/**
 * The useStoreSwitcher talon complements the StoreSwitcher component.
 *
 * @param {*} props.getStoreConfig the store switcher data getStoreConfig
 *
 * @returns {Map}    talonProps.availableStores - Details about the available store views.
 * @returns {String}    talonProps.currentStoreName - Name of the current store view.
 * @returns {Boolean}   talonProps.storeMenuIsOpen - Whether the menu that this trigger toggles is open or not.
 * @returns {Ref}       talonProps.storeMenuRef - A React ref to the menu that this trigger toggles.
 * @returns {Ref}       talonProps.storeMenuTriggerRef - A React ref to the trigger element itself.
 * @returns {Function}  talonProps.handleTriggerClick - A function for handling when the trigger is clicked.
 * @returns {Function}  talonProps.handleSwitchStore - A function for handling when the menu item is clicked.
 */

export var useStoreSwitcher = function () {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var operations = mergeOperations(DEFAULT_STORE_OPERATIONS, props.operations);
  var {
    getUrlResolverData
  } = operations;
  var {
    pathname,
    search: params
  } = useLocation();
  var getNewUrl = useAwaitQuery(getUrlResolverData);
  var currentStoreCode = getStoreCode();

  // availableStores => mapped options or empty map if undefined.
  var availableStores = useMemo(() => {
    return STORE_CONFIG_DATA && AVAILABLE_STORE_VIEWS ? mapAvailableOptions(STORE_CONFIG_DATA, AVAILABLE_STORE_VIEWS) : new Map();
  }, []);
  var emptyPath = !pathname || pathname === '/' || pathname === "/".concat(currentStoreCode) || pathname === "/".concat(currentStoreCode, "/");

  // Change store view code and currency to be used in Apollo link request headers
  var handleSwitchStore = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(function* (storeCode) {
      try {
        // Do nothing when store view is not present in available stores
        if (!availableStores.has(storeCode)) return;
        var oppositeStore = availableStores.get(storeCode);
        console.log(oppositeStore);
        var newPath = '/' + storeCode;
        var reactpath = react_paths.find(path => path[currentStoreCode] === removeLastSlash(pathname));
        if (!emptyPath) {
          if (!reactpath) {
            var {
              data: newUrlData
            } = yield getNewUrl({
              fetchPolicy: 'no-cache',
              variables: {
                url: pathname,
                storeFrom: currentStoreCode
              },
              context: {
                headers: {
                  'store': oppositeStore.code
                }
              }
            });
            newPath += '/' + (newUrlData && newUrlData.urlResolver.relative_url) + params;
          } else {
            //reactpath has slash included already
            newPath += reactpath[storeCode] + params;
          }
        }

        // Handle updating the URL if the store code should be present.
        // In this block we use `window.location.assign` to work around the
        // static React Router basename, which is changed on initialization.
        if (process.env.USE_STORE_CODE_IN_URL === 'true') {
          // Check to see if we're on a page outside of the homepage
          var [, pathStoreCode] = newPath.split('/');
          // If the current store code is in the url, replace it with
          // the new one.
          if (availableStores.has(pathStoreCode)) {
            window.location.assign(newPath);
          } else {
            window.location.assign("/".concat(storeCode));
          }
        } else {
          // Refresh the page to re-trigger the queries once code/currency
          // are saved in local storage.
          window.location.assign("".concat(newPath).concat(params));
        }
      } catch (e) {
        window.location.assign("/".concat(storeCode));
      }
    });
    return function handleSwitchStore(_x) {
      return _ref.apply(this, arguments);
    };
  }();
  return {
    availableStores,
    handleSwitchStore,
    currentStoreCode
  };
};